import React, { useState } from "react";
import {
  Calendar,
  Clock,
  Star,
  User,
  Filter,
  Search,
  Download,
  MessageSquare,
} from "lucide-react";
import { cn, formatDate, formatTime } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  useCounselorSessionHistory,
  useCounselorStats,
} from "@/hooks/useCounselorData";
import ChatSidebar from "@/components/chat/ChatSidebar";
import { ExportConfirmationDialog } from "@/components/ExportConfirmationDialog";
import { exportSessionsToCSV, type SessionData } from "@/utils/csvExport";
import { useToast } from "@/hooks/use-toast";

interface FilterOptions {
  status: "ALL" | "ENDED" | "ACTIVE" | "PENDING" | "CANCELLED";
  type: "ALL" | "CHAT" | "VOICE" | "VIDEO";
  dateRange: "ALL" | "TODAY" | "WEEK" | "MONTH" | "CUSTOM";
  sortBy: "DATE" | "STUDENT" | "DURATION" | "RATING";
  sortOrder: "ASC" | "DESC";
}

export const SessionHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    status: "ALL",
    type: "ALL",
    dateRange: "ALL",
    sortBy: "DATE",
    sortOrder: "DESC",
  });

  // API params based on filters
  const apiParams = {
    search: searchQuery || undefined,
    status: filters.status !== "ALL" ? filters.status.toLowerCase() : undefined,
    type: filters.type !== "ALL" ? filters.type.toLowerCase() : undefined,
    page: 1,
    limit: 100,
  };

  // Fetch session history
  const {
    data: sessionsData,
    isLoading,
    error,
    refetch,
  } = useCounselorSessionHistory(user?.id, apiParams);

  const allSessions = Array.isArray(sessionsData) ? sessionsData : [];

  const { data: counselorStats } = useCounselorStats(user?.id, "all");

  // Calculate session duration and add helper fields
  const processedSessions = allSessions.map((session: any) => ({
    ...session,
    studentName: session.student?.name || "Unknown Student",
    duration: session.duration ? Math.round(session.duration / 60) : 0, // Convert seconds to minutes
    messageCount: session.messages?.length || 0,
  }));

  // Filter and search sessions
  const filteredSessions = processedSessions
    .filter((session: any) => {
      const matchesSearch =
        searchQuery === "" ||
        (session.studentName &&
          session.studentName
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (session.student?.email &&
          session.student.email
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (session.feedback &&
          session.feedback.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (session.messages &&
          session.messages.some((msg: any) =>
            msg.message.toLowerCase().includes(searchQuery.toLowerCase())
          ));

      const matchesStatus =
        filters.status === "ALL" ||
        (session.status && session.status.toUpperCase() === filters.status);
      const matchesType =
        filters.type === "ALL" ||
        (session.sessionType &&
          session.sessionType.toUpperCase() === filters.type);

      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a: any, b: any) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case "DATE":
          comparison =
            new Date(a.startedAt || 0).getTime() -
            new Date(b.startedAt || 0).getTime();
          break;
        case "STUDENT":
          comparison = (a.studentName || "").localeCompare(b.studentName || "");
          break;
        case "DURATION":
          comparison = (a.duration || 0) - (b.duration || 0);
          break;
        case "RATING":
          comparison = (a.rating || 0) - (b.rating || 0);
          break;
      }

      return filters.sortOrder === "ASC" ? comparison : -comparison;
    });

  const getStatusColor = (status: string | undefined) => {
    if (!status) return "bg-gray-100 text-gray-800";
    switch (status.toLowerCase()) {
      case "ended":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "active":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (type: string | undefined) => {
    if (!type) return "bg-gray-100 text-gray-800";
    switch (type.toLowerCase()) {
      case "chat":
        return "bg-green-100 text-green-800";
      case "voice":
        return "bg-blue-100 text-blue-800";
      case "video":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const toggleSessionExpanded = (sessionId: string) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  const handleExportData = () => {
    setShowExportDialog(true);
  };

  const handleConfirmExport = async () => {
    setIsExporting(true);
    try {
      // Convert filtered sessions to the expected format
      const sessionsToExport: SessionData[] = filteredSessions.map(
        (session: any) => ({
          id: session.id,
          sessionType: session.sessionType,
          callType: session.callType,
          status: session.status,
          duration: session.duration,
          createdAt: session.createdAt,
          updatedAt: session.updatedAt,
          startedAt: session.startedAt,
          endedAt: session.endedAt,
          studentId: session.studentId,
          counselorId: session.counselorId,
          student: {
            id: session.student?.id,
            name: session.student?.name,
            email: session.student?.email,
            phoneNumber: session.student?.phoneNumber,
            grade: session.student?.grade,
            schoolName: session.student?.schoolName,
            profilePicture: session.student?.profilePicture,
            interests: session.student?.interests || [],
            bio: session.student?.bio,
          },
          messages: session.messages || [],
          rating: session.rating,
          feedback: session.feedback,
          recordingUrl: session.recordingUrl,
          provider: session.provider,
          meetingId: session.meetingId,
        })
      );

      // Generate filename with current date and session count
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `counseling_sessions_${currentDate}_${sessionsToExport.length}_records.csv`;

      // Export to CSV
      await exportSessionsToCSV(sessionsToExport, filename);

      // Show success toast
      toast({
        title: "Export Successful",
        description: `Successfully exported ${sessionsToExport.length} session${
          sessionsToExport.length !== 1 ? "s" : ""
        } to CSV file.`,
      });

      setShowExportDialog(false);
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: "Export Failed",
        description:
          "There was an error exporting your sessions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancelExport = () => {
    setShowExportDialog(false);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error loading sessions
            </h3>
            <p className="text-gray-600 mb-4">
              There was an error loading your session history.
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalSessions = filteredSessions.length;
  const completedSessions = filteredSessions.filter(
    (s: any) => s.status && s.status.toLowerCase() === "ended"
  ).length;

  const ratingsArray = filteredSessions.filter(
    (s: any) => s.rating && s.rating > 0
  );
  const averageRating =
    ratingsArray.length > 0
      ? ratingsArray.reduce((sum: number, s: any) => sum + (s.rating || 0), 0) /
        ratingsArray.length
      : 0;

  const selectedSession = expandedSession
    ? filteredSessions.find((s) => s.id === expandedSession)
    : null;

  return (
    <div className="flex h-screen overflow-hidden">
      <div
        className={cn(
          "flex-1 overflow-auto transition-all duration-300",
          expandedSession ? "mr-[30rem]" : ""
        )}
      >
        <div className="space-y-6 p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Session History
              </h1>
              <p className="text-gray-600 mt-1">
                Review and manage your counseling sessions
              </p>
            </div>
            <button
              onClick={handleExportData}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mt-4 sm:mt-0"
            >
              <Download className="h-4 w-4" />
              <span>Export Data</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Sessions
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {totalSessions}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {completedSessions}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Call Duration
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {`${Math.round(
                      counselorStats?.audioAndVideoCallDuration || 0
                    )}m`}{" "}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Avg Session Rating
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {averageRating.toFixed(1)}
                  </p>
                </div>
                <Star className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by student name, topics, or summary..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 border rounded-lg transition-colors",
                  showFilters
                    ? "border-primary bg-primary text-white"
                    : "border-gray-300 hover:bg-gray-50"
                )}
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
              </button>

              <div className="text-sm text-gray-600">
                {filteredSessions.length} sessions
              </div>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: e.target.value as FilterOptions["status"],
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="ALL">All Status</option>
                      <option value="ENDED">Ended</option>
                      <option value="ACTIVE">Active</option>
                      <option value="PENDING">Pending</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={filters.type}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          type: e.target.value as FilterOptions["type"],
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="ALL">All Types</option>
                      <option value="CHAT">Chat Session</option>
                      <option value="VOICE">Voice Call</option>
                      <option value="VIDEO">Video Call</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort By
                    </label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          sortBy: e.target.value as FilterOptions["sortBy"],
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="DATE">Date</option>
                      <option value="STUDENT">Student</option>
                      <option value="DURATION">Duration</option>
                      <option value="RATING">Rating</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order
                    </label>
                    <select
                      value={filters.sortOrder}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          sortOrder: e.target
                            .value as FilterOptions["sortOrder"],
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="DESC">Newest First</option>
                      <option value="ASC">Oldest First</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sessions List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="divide-y divide-gray-200">
              {filteredSessions.map((session: any) => (
                <div key={session.id} className="p-6">
                  {/* Session Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <User className="h-5 w-5 text-gray-400" />
                        <h3 className="font-semibold text-gray-900">
                          {session.studentName}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {session.student?.email}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getStatusColor(session.status)
                        )}
                      >
                        {session.status
                          ? session.status.charAt(0).toUpperCase() +
                            session.status.slice(1)
                          : "Unknown"}
                      </span>
                      <span
                        className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getTypeColor(session.sessionType)
                        )}
                      >
                        {session.sessionType
                          ? session.sessionType.charAt(0).toUpperCase() +
                            session.sessionType.slice(1)
                          : "Unknown"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right text-sm text-gray-600">
                        <p>{formatDate(session.startedAt)}</p>
                        <p>
                          {formatTime(session.startedAt)} • {session.duration}
                          min
                        </p>
                      </div>
                      {/* <button
                    onClick={() => toggleSessionExpanded(session.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedSession === session.id ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </button> */}
                    </div>
                  </div>

                  {/* Session Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center space-x-4">
                      {session.sessionType === "chat" && (
                        <div className="flex items-center space-x-1">
                          <MessageSquare className="h-4 w-4 text-blue-400" />
                          <span>{session.messageCount} messages</span>
                        </div>
                      )}
                      {session.rating && (
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span>{session.rating}/5</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-green-400" />
                        <span>{session.duration} minutes</span>
                      </div>
                    </div>
                    {session.sessionType === "chat" && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleSessionExpanded(session.id)}
                          className="flex items-center space-x-1 px-3 py-1 text-primary hover:text-primary/80 transition-colors"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span>
                            {expandedSession === session.id
                              ? "Close Chat"
                              : "Open Chat"}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Student Feedback */}
                  {session.feedback && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-1">
                        Student Feedback
                      </h4>
                      <p className="text-sm text-blue-800">
                        {session.feedback}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Empty state */}
            {filteredSessions.length === 0 && (
              <div className="p-12 text-center">
                <Calendar className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No sessions found
                </h3>
                <p className="text-gray-600">
                  {searchQuery ||
                  filters.status !== "ALL" ||
                  filters.type !== "ALL"
                    ? "Try adjusting your search or filters"
                    : "Your session history will appear here"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Sidebar */}
        {expandedSession && selectedSession && (
          <div className="fixed top-0 right-0 h-screen z-50">
            <ChatSidebar
              student={selectedSession.student}
              sessionId={selectedSession.id}
              sessionStatus={selectedSession.status}
              onClose={() => setExpandedSession(null)}
              onStartVoiceCall={() => {
                // Start voice call for session
              }}
              onStartVideoCall={() => {
                // Start video call for session
              }}
            />
          </div>
        )}
      </div>

      {/* Export Confirmation Dialog */}
      <ExportConfirmationDialog
        isOpen={showExportDialog}
        onClose={handleCancelExport}
        onConfirm={handleConfirmExport}
        sessionCount={filteredSessions.length}
        isExporting={isExporting}
      />
    </div>
  );
};
